// The account menu: who is signed in, impersonation for the accounts allowed to use it, Pengaturan
// and logout. Replaces the rail footer's account row, impersonation menu, Pengaturan row and logout.
import { ChevronDownIcon, DramaIcon, LogOutIcon, SettingsIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import ImpersonationSelect from '@components/impersonation/ImpersonationSelect';
import { useSidebarIdentity } from '@components/sidebar/sidebar-v2';
import useAuth from '@hooks/use-auth';
import { useHaptic } from '@utils/haptic';
import { Badge } from '@/components/lumen/badge';
import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/lumen/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/lumen/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

/** The display name from `authData`, kept in step with the same events `useSidebarIdentity` follows. */
function useAccountName() {
  const [name, setName] = useState('');
  useEffect(() => {
    const read = () => {
      try {
        const parsed = JSON.parse(localStorage.getItem('authData') || 'null');
        setName(typeof parsed?.name === 'string' ? parsed.name : '');
      } catch {
        setName('');
      }
    };
    read();
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'authData') read();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('impersonationChanged', read);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('impersonationChanged', read);
    };
  }, []);
  return name;
}

export function AccountMenu() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { logout, isLoading } = useAuth();
  const { userEmail, canImpersonate, isImpersonating } = useSidebarIdentity();
  const name = useAccountName();
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hapticMedium = useHaptic('medium');
  const hapticFirm = useHaptic('firm');

  const displayName = name || userEmail;
  const initial = (displayName || '?').charAt(0).toUpperCase();
  const impersonateLabel = isImpersonating ? t('sidebar.impersonating') : t('sidebar.impersonate');

  // Same call and callback as the old rail's logout.
  const handleLogout = () => {
    hapticFirm();
    logout(() => {
      toast.success('Success', { description: 'Logout success!' });
      navigate('/auth');
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            ref={triggerRef}
            variant="ghost"
            aria-label={`${t('lumen.topbar.account')}: ${userEmail}`}
            // Avatar only on phones; name and chevron from `md` up.
            className="h-8 w-8 gap-2 rounded-control px-0 text-fg has-[>svg]:px-0 data-[state=open]:bg-tint md:w-auto md:max-w-56 md:pr-2 md:pl-1 md:has-[>svg]:pr-2"
          >
            <span
              aria-hidden
              className={cn(
                'flex size-6.5 shrink-0 items-center justify-center rounded-full bg-surface-2 font-bold text-fg text-xs',
                isImpersonating && 'bg-warn/15 text-warn-ink ring-1 ring-warn/50'
              )}
            >
              {initial}
            </span>
            <span className="hidden min-w-0 truncate text-13 md:inline">{displayName}</span>
            <ChevronDownIcon className="hidden text-fg-muted md:block" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="flex flex-col gap-0.5 py-2 font-normal">
            {name && <span className="truncate font-semibold text-fg text-sm">{name}</span>}
            <span className="truncate text-fg-muted text-xs" title={userEmail}>
              {userEmail}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {canImpersonate && (
            <DropdownMenuItem
              onSelect={() => setImpersonateOpen(true)}
              className={cn(isImpersonating && 'text-warn focus:text-warn')}
            >
              <DramaIcon className={cn(isImpersonating && 'text-warn')} aria-hidden />
              <span className="flex-1">{impersonateLabel}</span>
              {isImpersonating && <Badge aria-hidden variant="warn" className="size-2 rounded-full p-0" />}
            </DropdownMenuItem>
          )}
          {/* Personal settings: out of the menu tree and the module list on purpose, so no role or
              company lock can take it away. */}
          <DropdownMenuItem
            onSelect={() => {
              hapticMedium();
              navigate('/pengaturan');
            }}
          >
            <SettingsIcon aria-hidden />
            {t('menu.pengaturan')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            disabled={isLoading}
            onSelect={(event) => {
              // Keep the menu open so the in-flight label shows until the session is cleared.
              event.preventDefault();
              handleLogout();
            }}
          >
            <LogOutIcon aria-hidden />
            {isLoading ? t('sidebar.loggingOut') : t('sidebar.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {canImpersonate && (
        <Dialog open={impersonateOpen} onOpenChange={setImpersonateOpen}>
          {/* Focus the dialog, not the user search. Focusing the search opens its list, a modal Radix
              popover that never holds focus itself; the closing account menu then sends focus back to its
              trigger, outside the dialog, and that popover's trap cannot bring it back. Focused on the
              dialog, its own trap does. Tab or a click reaches the search. */}
          <DialogContent
            className="max-w-md"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              (event.currentTarget as HTMLElement | null)?.focus();
            }}
            // Opened from a menu item, so Radix has no trigger to return focus to: send it to the account button.
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              triggerRef.current?.focus();
            }}
          >
            <DialogHeader>
              <DialogTitle>{impersonateLabel}</DialogTitle>
              <DialogDescription>{t('lumen.topbar.impersonateDescription')}</DialogDescription>
            </DialogHeader>
            {/* The existing control: user search, start and stop, page reload. */}
            <ImpersonationSelect />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
